import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {CupertinoPane, CupertinoSettings} from 'cupertino-pane';
import {CommonModule, DOCUMENT} from '@angular/common';
import {ButtonModule} from 'primeng/button';
import {WindowProvider} from '../../../core/providers/window.provider';
import {Keyboard as CapKeyboard} from '@capacitor/keyboard';
import {Capacitor} from '@capacitor/core';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';

export interface BottomSheetDragMetrics {
  progress: number;
  translateY: number;
  visibleHeight: number;
}

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss'
})
export class BottomSheetComponent implements AfterViewInit, OnDestroy {
  private static readonly instances: Set<BottomSheetComponent> = new Set<BottomSheetComponent>();
  private static readonly KEYBOARD_OPEN_INSET_THRESHOLD_PX = 80;

  @Output() public dragProgressChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() public dragMetricsChange: EventEmitter<BottomSheetDragMetrics> = new EventEmitter<BottomSheetDragMetrics>();
  @Output() public dismissed: EventEmitter<void> = new EventEmitter<void>();
  @Output() public backButtonClick: EventEmitter<void> = new EventEmitter<void>();
  @Input() public backdrop: boolean = false;
  @Input() public dismissOnBackdrop: boolean = false;
  @Input() public closeButtonLabel: string | null = null;
  @Input() public backButtonLabel: string | null = null;
  /**
   * An optional content-aware middle snap point. Consumers that omit it retain
   * the established 70vh behaviour used by the existing sheets.
   */
  @Input() public middleBreakHeight: number | null = null;
  @ViewChild('bottomSheetContainer') bottomSheetContainer!: ElementRef<HTMLElement>;

  public bottomSheet: CupertinoPane;
  private reqFrameId: number;
  private paneInitPromise: Promise<void> | null = null;
  private resolvePaneInit: (() => void) | null = null;
  private paneEl: HTMLElement | null = null;
  private bodyScrollLocked: boolean = false;
  private envSafeAreaInsets: {top: number; bottom: number} = {top: 0, bottom: 0};
  private movedToTopForKeyboard: boolean = false;
  private isDraggingHandle: boolean = false;
  private activeTouchStartY: number | null = null;
  private activeTouchScrollableEl: HTMLElement | null = null;
  private readonly onDocumentTouchMoveOutsidePane = (event: TouchEvent): void => {
    if (!this.backdrop || !this.paneEl || !this.isPresented()) {
      return;
    }

    const target = event.target as Node | null;
    // Image previews are appended to body above the pane and own their gestures.
    if ((target as Element | null)?.closest?.('.p-image-mask, .app-image-lightbox-mask')) {
      return;
    }
    if (target && this.paneEl.contains(target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };
  private readonly onPaneTouchStart = (event: TouchEvent): void => {
    const target = event.target as HTMLElement | null;
    this.activeTouchStartY = event.touches?.[0]?.clientY ?? null;
    this.activeTouchScrollableEl = this.findScrollableAncestor(target);
  };
  private readonly onPaneTouchMove = (event: TouchEvent): void => {
    if (!this.isKeyboardOpen()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const withinHandle = !!target?.closest('.bottom-sheet-drag-handle');
    if (withinHandle) {
      return;
    }

    const currentTouchY = event.touches?.[0]?.clientY;
    const startY = this.activeTouchStartY ?? currentTouchY ?? 0;
    const deltaY = (currentTouchY ?? startY) - startY;
    const scrollable = this.activeTouchScrollableEl ?? this.findScrollableAncestor(target);
    if (scrollable && this.canScrollableConsumeGesture(scrollable, deltaY)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };
  private readonly onPaneTouchEnd = (): void => {
    this.activeTouchStartY = null;
    this.activeTouchScrollableEl = null;
  };
  private readonly onPaneFocusIn = (event: FocusEvent): void => {
    if ((this.window.innerWidth || 0) >= 768) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!this.isEditableTarget(target)) {
      return;
    }

    const currentBreak = this.bottomSheet?.currentBreak?.();
    if (currentBreak === 'top') {
      this.movedToTopForKeyboard = true;
      this.syncPaneHiddenBottom();
      return;
    }

    this.movedToTopForKeyboard = false;
    this.ensureTopBreakpointForKeyboard();
    this.syncPaneHiddenBottom();
  };
  private readonly onViewportResize = (): void => {
    if (!this.paneEl || !this.bottomSheet?.isPanePresented()) {
      return;
    }
    if (this.isDraggingHandle) {
      this.syncPaneHiddenBottom();
      this.emitPaneProgress();
      return;
    }
    this.refreshEnvSafeAreaInsets();
    const keyboardOpen = this.isKeyboardOpen();
    if (!keyboardOpen) {
      this.movedToTopForKeyboard = false;
      this.enforcePaneViewportBounds();
    } else {
      this.ensureTopBreakpointForKeyboard();
      this.enforcePaneViewportBounds();
      this.syncPaneHiddenBottom();
    }
    this.emitPaneProgress();
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WindowProvider) private window: Window
  ) {
  }

  ngAfterViewInit(): void {
    BottomSheetComponent.instances.add(this);
    this.paneInitPromise = new Promise<void>((resolve) => {
      this.resolvePaneInit = resolve;
    });
    this.reqFrameId = requestAnimationFrame(() => {
      this.refreshEnvSafeAreaInsets();
      const settings: CupertinoSettings = {
        parentElement: this.document.body,
        breaks: {
          middle: {height: this.getInitialMiddleBreakpointHeight(), enabled: true, bounce: true},
          top: {height: this.getInitialTopBreakpointHeight(), enabled: true, bounce: true}
        },
        initialBreak: 'middle',
        fitScreenHeight: false,
        upperThanTop: false,
        freeMode: false,
        backdrop: this.backdrop,
        bottomClose: true,
        buttonDestroy: false,
        simulateTouch: true,
        touchMoveStopPropagation: true,
        topperOverflow: false,
        preventClicks: false,
        dragBy: ['.pane .bottom-sheet-drag-handle'],
        handleKeyboard: false,
        showDraggable: false,
        events: {
          onBackdropTap: () => {
            if (this.dismissOnBackdrop) {
              void this.closeIfPresented();
            }
          },
          onDragStart: () => {
            this.isDraggingHandle = true;
            if (this.isKeyboardOpen()) {
              this.dismissKeyboard();
            }
          },
          onDrag: () => {
            this.enforcePaneViewportBounds();
            this.emitPaneProgress();
          },
          onDragEnd: () => {
            this.isDraggingHandle = false;
            this.enforcePaneViewportBounds();
            this.emitPaneProgress();
          },
          onTransitionStart: () => {
            this.enforcePaneViewportBounds();
            this.emitPaneProgress();
          },
          onTransitionEnd: () => {
            this.enforcePaneViewportBounds();
            this.emitPaneProgress();
          },
          onDidPresent: () => {
            this.refreshEnvSafeAreaInsets();
            this.paneEl = this.bottomSheet?.paneEl ?? null;
            this.applyPaneTheme();
            this.paneEl?.addEventListener('touchstart', this.onPaneTouchStart, {passive: true, capture: true});
            this.paneEl?.addEventListener('touchmove', this.onPaneTouchMove, {passive: false, capture: true});
            this.paneEl?.addEventListener('touchend', this.onPaneTouchEnd, {passive: true, capture: true});
            this.paneEl?.addEventListener('touchcancel', this.onPaneTouchEnd, {passive: true, capture: true});
            this.paneEl?.addEventListener('focusin', this.onPaneFocusIn, true);
            this.document.addEventListener('touchmove', this.onDocumentTouchMoveOutsidePane, {passive: false, capture: true});
            this.lockBodyScroll();
            this.detachCupertinoKeyboardResizeHandler();
            const content = this.paneEl?.querySelector('.content-overflow-y') as HTMLElement | null;
            const input = this.paneEl?.querySelector('.input-overflow-y') as HTMLElement | null;
            const isMobile = (this.window.innerWidth || 0) < 768;

            if (content && this.paneEl) {
              if (isMobile) {
                content.style.overflowY = 'visible';
                content.style.maxHeight = '';
              } else {
                content.style.overflowY = 'auto';
                content.style.maxHeight = `${this.paneEl.clientHeight - content.offsetTop}px`;
              }
            }

            if (input && this.paneEl) {
              if (isMobile) {
                input.style.overflowY = 'hidden';
                input.style.maxHeight = `${this.paneEl.clientHeight - input.offsetTop}px`;
              } else {
                input.style.overflowY = 'visible';
                input.style.maxHeight = '';
              }
            }

            this.enforcePaneViewportBounds();
            this.emitPaneProgress();
          },
          onDidDismiss: () => {
            this.dismissKeyboard();
            this.unlockBodyScroll();
            this.isDraggingHandle = false;
            this.paneEl?.removeEventListener('touchstart', this.onPaneTouchStart, true);
            this.paneEl?.removeEventListener('touchmove', this.onPaneTouchMove, true);
            this.paneEl?.removeEventListener('touchend', this.onPaneTouchEnd, true);
            this.paneEl?.removeEventListener('touchcancel', this.onPaneTouchEnd, true);
            this.paneEl?.removeEventListener('focusin', this.onPaneFocusIn, true);
            this.document.removeEventListener('touchmove', this.onDocumentTouchMoveOutsidePane, true);
            this.onPaneTouchEnd();
            this.paneEl?.style.removeProperty('--pane-hidden-bottom');
            this.paneEl = null;
            this.movedToTopForKeyboard = false;
            this.emitPaneProgress();
            this.dismissed.emit();
          }
        }
      };

      this.bottomSheet = new CupertinoPane(this.bottomSheetContainer.nativeElement, settings);
      this.resolvePaneInit?.();
      this.resolvePaneInit = null;
    });

    this.window.addEventListener('resize', this.onViewportResize, {passive: true});
    this.window.visualViewport?.addEventListener('resize', this.onViewportResize, {passive: true});
  }

  public async toggleBottomSheet() {
    await this.ensurePaneInitialized();
    if (!this.bottomSheet) {
      return;
    }

    if (this.isPresented()) {
      await this.closeIfPresented(true);
      return;
    }
    await this.bottomSheet.present({animate: true});
    await this.bottomSheet.moveToBreak('middle');
  }

  public async moveToTop() {
    await this.ensurePaneInitialized();
    if (!this.bottomSheet) {
      return;
    }
    await this.bottomSheet.moveToBreak('top');
  }

  public async moveToMiddle() {
    await this.ensurePaneInitialized();
    if (!this.bottomSheet) {
      return;
    }
    await this.bottomSheet.moveToBreak('middle');
  }

  public async updateMiddleBreakHeight(height: number): Promise<void> {
    this.middleBreakHeight = height;
    await this.ensurePaneInitialized();
    if (!this.bottomSheet || !this.isPresented()) {
      return;
    }

    await this.bottomSheet.setBreakpoints({
      middle: {height: this.getInitialMiddleBreakpointHeight(), enabled: true, bounce: true}
    });
    this.enforcePaneViewportBounds();
  }

  public async closeIfPresented(animate: boolean = true): Promise<void> {
    await this.destroyIfPresented(animate);
  }

  public static async closeAllPresented(except?: BottomSheetComponent): Promise<void> {
    const tasks = Array.from(BottomSheetComponent.instances)
      .filter((instance) => instance !== except)
      .map((instance) => instance.destroyIfPresented());

    if (!tasks.length) {
      return;
    }

    await Promise.allSettled(tasks);
  }

  public static hasAnyPresented(except?: BottomSheetComponent): boolean {
    return Array.from(BottomSheetComponent.instances)
      .filter((instance) => instance !== except)
      .some((instance) => instance.isPresented());
  }

  public isPresented(): boolean {
    if (!this.bottomSheet) {
      return false;
    }

    try {
      return this.bottomSheet.isPanePresented() === true;
    } catch {
      return false;
    }
  }

  public getPaneTransformY() {
    const paneEl = this.paneEl;
    if (!paneEl || !paneEl.style.transform) {
      return 0;
    }

    const translateYRegex = /\.*translateY\((.*)px\)/i;
    const match = translateYRegex.exec(paneEl.style.transform);
    return match ? parseFloat(match[1]) : 0;
  }

  private emitPaneProgress(): void {
    if (!this.paneEl || !this.bottomSheet?.isPanePresented()) {
      this.dragProgressChange.emit(0);
      this.dragMetricsChange.emit({progress: 0, translateY: this.getViewportHeight(), visibleHeight: 0});
      return;
    }

    const viewportHeight = this.getViewportHeight() || 1;
    const translateY = this.getPaneTransformY();
    const normalized = Math.min(1, Math.max(0, 1 - (translateY / viewportHeight)));
    const visibleHeight = Math.min(viewportHeight, Math.max(0, viewportHeight - translateY));

    this.dragProgressChange.emit(normalized);
    this.dragMetricsChange.emit({progress: normalized, translateY, visibleHeight});
  }

  private getInitialTopBreakpointHeight(): number {
    const viewportHeight = this.getLayoutViewportHeight();
    const safeTop = this.getSafeAreaInsetTop();
    return Math.max(120, viewportHeight - safeTop);
  }

  private getInitialMiddleBreakpointHeight(): number {
    const viewportHeight = this.getLayoutViewportHeight();
    const safeBottom = this.getSafeAreaInsetBottom();
    const topHeight = this.getInitialTopBreakpointHeight();
    const maxMiddleHeight = Math.max(160, topHeight - 56);
    const configuredHeight = Number(this.middleBreakHeight);
    const hasConfiguredHeight = Number.isFinite(configuredHeight) && configuredHeight > 0;
    const preferred = hasConfiguredHeight ? configuredHeight : viewportHeight * 0.7;
    const minVisible = (hasConfiguredHeight ? 160 : 320) + safeBottom;
    return Math.max(160, Math.min(maxMiddleHeight, Math.max(preferred, minVisible)));
  }

  private getViewportHeight(): number {
    return this.window.visualViewport?.height || this.window.innerHeight || 0;
  }

  private getLayoutViewportHeight(): number {
    return this.window.innerHeight || this.getViewportHeight();
  }

  private enforcePaneViewportBounds(): void {
    if (!this.paneEl) {
      return;
    }
    const safeTop = this.getSafeAreaInsetTop();
    const viewportHeight = this.getLayoutViewportHeight();
    const maxPaneHeight = Math.max(120, viewportHeight - safeTop);
    this.paneEl.style.maxHeight = `${maxPaneHeight}px`;

    const translateY = this.getPaneTransformY();
    if (Number.isFinite(translateY) && translateY < safeTop) {
      this.paneEl.style.transform = `translateY(${safeTop}px) translateZ(0px)`;
    }

    const paneRect = this.paneEl.getBoundingClientRect();
    const hiddenBottomViewportHeight = this.getPaneHiddenBottomViewportHeight();
    const hiddenBottom = Math.max(0, paneRect.bottom - hiddenBottomViewportHeight);
    this.paneEl.style.setProperty('--pane-hidden-bottom', `${hiddenBottom}px`);
  }

  private syncPaneHiddenBottom(): void {
    if (!this.paneEl) {
      return;
    }

    const paneRect = this.paneEl.getBoundingClientRect();
    const hiddenBottomViewportHeight = this.getPaneHiddenBottomViewportHeight();
    const hiddenBottom = Math.max(0, paneRect.bottom - hiddenBottomViewportHeight);
    this.paneEl.style.setProperty('--pane-hidden-bottom', `${hiddenBottom}px`);
  }

  private getPaneHiddenBottomViewportHeight(): number {
    if (!this.isKeyboardOpen()) {
      return this.getLayoutViewportHeight();
    }

    const visualViewport = this.window.visualViewport;
    if (!visualViewport) {
      return this.getLayoutViewportHeight();
    }

    return visualViewport.height + visualViewport.offsetTop;
  }

  private ensureTopBreakpointForKeyboard(): void {
    if (!this.bottomSheet || !this.isPresented() || this.movedToTopForKeyboard) {
      return;
    }
    const currentBreak = this.bottomSheet.currentBreak?.();
    if (currentBreak === 'top') {
      this.movedToTopForKeyboard = true;
      return;
    }

    this.movedToTopForKeyboard = true;
    // Use non-animated move while keyboard appears to avoid visible bounce/jump.
    void this.bottomSheet.moveToBreak('top', 'move').then(() => {
      this.enforcePaneViewportBounds();
      this.emitPaneProgress();
    }).catch(() => {
      this.movedToTopForKeyboard = false;
    });
  }

  private isKeyboardOpen(): boolean {
    const cssKeyboardHeightValue = this.readCssValue('--keyboard-height');
    if (cssKeyboardHeightValue.length > 0) {
      return this.parsePx(cssKeyboardHeightValue) > 0;
    }

    if (this.getKeyboardHeight() > 0) {
      return true;
    }

    return this.getVisualViewportKeyboardInset() >= BottomSheetComponent.KEYBOARD_OPEN_INSET_THRESHOLD_PX;
  }

  private getVisualViewportKeyboardInset(): number {
    const visualViewport = this.window.visualViewport;
    if (!visualViewport) {
      return 0;
    }

    const layoutHeight = this.getLayoutViewportHeight();
    const visualBottom = visualViewport.height + visualViewport.offsetTop;
    return Math.max(0, layoutHeight - visualBottom);
  }

  private isEditableElement(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }

    if (element.isContentEditable) {
      return true;
    }

    const tagName = element.tagName?.toLowerCase?.();
    return tagName === 'input' || tagName === 'textarea';
  }

  private isEditableTarget(target: HTMLElement | null): boolean {
    if (!target) {
      return false;
    }

    if (this.isEditableElement(target)) {
      return true;
    }

    return !!target.closest('input, textarea, [contenteditable]');
  }

  private getSafeAreaInsetTop(): number {
    const cssVarValue = this.readCssPx('--safe-area-inset-top');
    const resolved = Math.max(cssVarValue, this.envSafeAreaInsets.top);
    if (Capacitor.getPlatform() === 'ios' && resolved < 1) {
      return 44;
    }
    return resolved;
  }

  private getSafeAreaInsetBottom(): number {
    const cssVarValue = this.readCssPx('--safe-area-inset-bottom');
    const resolved = Math.max(cssVarValue, this.envSafeAreaInsets.bottom);
    if (Capacitor.getPlatform() === 'ios' && resolved < 1) {
      return 34;
    }
    return resolved;
  }

  private refreshEnvSafeAreaInsets(): void {
    if (!this.document.body) {
      return;
    }

    const probe = this.document.createElement('div');
    probe.style.position = 'fixed';
    probe.style.top = '0';
    probe.style.left = '0';
    probe.style.width = '0';
    probe.style.height = '0';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.paddingTop = 'env(safe-area-inset-top)';
    probe.style.paddingBottom = 'env(safe-area-inset-bottom)';
    this.document.body.appendChild(probe);

    const computedStyle = getComputedStyle(probe);
    this.envSafeAreaInsets.top = this.parsePx(computedStyle.paddingTop);
    this.envSafeAreaInsets.bottom = this.parsePx(computedStyle.paddingBottom);
    probe.remove();
  }

  private readCssPx(variableName: string): number {
    return this.parsePx(this.readCssValue(variableName));
  }

  private readCssValue(variableName: string): string {
    const computedStyle = getComputedStyle(this.document.documentElement);
    return (computedStyle.getPropertyValue(variableName) || '').trim();
  }

  private parsePx(value: string): number {
    const parsed = Number.parseFloat((value || '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private detachCupertinoKeyboardResizeHandler(): void {
    const keyboardResizeCb = (this.bottomSheet as any)?.keyboardEvents?.onWindowResizeCb;
    if (typeof keyboardResizeCb === 'function') {
      this.window.removeEventListener('resize', keyboardResizeCb);
    }
  }

  private applyPaneTheme(): void {
    if (!this.paneEl) {
      return;
    }

    this.paneEl.style.setProperty('--cupertino-pane-background', 'var(--surface-overlay)');
    this.paneEl.style.setProperty('--cupertino-pane-destroy-button-background', 'var(--surface-overlay)');
    this.paneEl.style.setProperty('--cupertino-pane-color', 'var(--text-color)');
    this.paneEl.style.backgroundColor = 'var(--cupertino-pane-background)';
    this.paneEl.style.color = 'var(--cupertino-pane-color)';
  }

  private getKeyboardHeight(): number {
    return this.readCssPx('--keyboard-height');
  }

  private dismissKeyboard(): void {
    const activeElement = this.document.activeElement as HTMLElement | null;
    activeElement?.blur?.();

    if (Capacitor.getPlatform() !== 'web') {
      void CapKeyboard.hide().catch(() => {});
    }
  }

  ngOnDestroy(): void {
    BottomSheetComponent.instances.delete(this);
    this.unlockBodyScroll();
    this.resolvePaneInit?.();
    this.resolvePaneInit = null;
    this.window.removeEventListener('resize', this.onViewportResize);
    this.window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    this.dragProgressChange.emit(0);
    this.dragMetricsChange.emit({progress: 0, translateY: this.window.innerHeight || 0, visibleHeight: 0});
    this.paneEl?.removeEventListener('touchstart', this.onPaneTouchStart, true);
    this.paneEl?.removeEventListener('touchmove', this.onPaneTouchMove, true);
    this.paneEl?.removeEventListener('touchend', this.onPaneTouchEnd, true);
    this.paneEl?.removeEventListener('touchcancel', this.onPaneTouchEnd, true);
    this.paneEl?.removeEventListener('focusin', this.onPaneFocusIn, true);
    this.document.removeEventListener('touchmove', this.onDocumentTouchMoveOutsidePane, true);
    this.onPaneTouchEnd();
    this.paneEl = null;
    void this.destroyIfPresented(false);
    if (typeof this.reqFrameId === 'number') {
      cancelAnimationFrame(this.reqFrameId);
    }
  }

  private async destroyIfPresented(animate: boolean = false): Promise<void> {
    if (!this.bottomSheet || !this.isPresented()) {
      return;
    }

    this.dismissKeyboard();
    this.dragProgressChange.emit(0);
    this.dragMetricsChange.emit({progress: 0, translateY: this.window.innerHeight || 0, visibleHeight: 0});
    try {
      await this.bottomSheet.destroy({animate});
    } catch (error) {
      const message = String(error ?? '');
      if (!message.includes('Present pane before call destroy()')) {
        throw error;
      }
    }
  }

  private lockBodyScroll(): void {
    if (this.bodyScrollLocked || !this.document.body) {
      return;
    }

    disableBodyScroll(this.document.body);
    this.bodyScrollLocked = true;
  }

  private unlockBodyScroll(): void {
    if (!this.bodyScrollLocked || !this.document.body) {
      return;
    }

    enableBodyScroll(this.document.body);
    this.bodyScrollLocked = false;
  }

  private findScrollableAncestor(target: HTMLElement | null): HTMLElement | null {
    if (!target || !this.paneEl) {
      return null;
    }

    let current: HTMLElement | null = target;
    while (current && current !== this.paneEl) {
      if (this.isVerticallyScrollable(current)) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }

  private isVerticallyScrollable(element: HTMLElement): boolean {
    const styles = this.window.getComputedStyle(element);
    const overflowY = styles.overflowY;
    const canScrollY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    return canScrollY && element.scrollHeight > element.clientHeight + 1;
  }

  private canScrollableConsumeGesture(element: HTMLElement, deltaY: number): boolean {
    const scrollTop = element.scrollTop;
    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
    if (maxScrollTop < 1) {
      return false;
    }

    if (deltaY > 0) {
      return scrollTop > 0;
    }

    if (deltaY < 0) {
      return scrollTop < maxScrollTop;
    }

    return true;
  }

  private async ensurePaneInitialized(): Promise<void> {
    if (this.bottomSheet) {
      return;
    }

    if (this.paneInitPromise) {
      await this.paneInitPromise;
    }
  }
}
